import type {
  AccessBindingResource,
  AccessBindingTargetRef,
} from 'adp-access-common';

export type BindingTargetKind = 'Component' | 'Cluster' | 'Project';

export type BindingFormTarget = {
  targetKind: BindingTargetKind;
  targetRef: string;
};

function normalizeEntityRef(ref: string): string {
  return ref.trim().toLocaleLowerCase('en-US');
}

export function filterRelatedProjectOptions<T extends { ref: string }>(
  options: T[],
  relatedProjectRefs?: readonly string[],
): T[] {
  if (relatedProjectRefs === undefined) {
    return options;
  }

  const relatedRefs = new Set(relatedProjectRefs.map(normalizeEntityRef));
  return options.filter(option =>
    relatedRefs.has(normalizeEntityRef(option.ref)),
  );
}

export function getBindingTargetKind(
  targetRef: AccessBindingTargetRef | undefined,
  projectRefs: readonly string[] = [],
): BindingTargetKind {
  if (targetRef?.kind !== 'Resource') {
    return 'Component';
  }

  const normalizedRef = normalizeEntityRef(targetRef.ref);
  if (
    projectRefs.some(
      projectRef => normalizeEntityRef(projectRef) === normalizedRef,
    ) ||
    normalizedRef.startsWith('resource:kubernetes-projects/')
  ) {
    return 'Project';
  }

  return 'Cluster';
}

export function resolveBindingFormTarget(
  binding: AccessBindingResource | undefined,
  lockedTargetRef?: string,
  defaultTargetRef?: string,
  projectRefs: readonly string[] = [],
): BindingFormTarget {
  const targetRef =
    lockedTargetRef ??
    binding?.spec.targetRef?.ref ??
    (!binding ? defaultTargetRef : '') ??
    '';

  return {
    targetKind: getBindingTargetKind(
      lockedTargetRef
        ? {
            kind: lockedTargetRef
              .toLocaleLowerCase('en-US')
              .startsWith('resource:')
              ? 'Resource'
              : 'Component',
            ref: lockedTargetRef,
          }
        : binding?.spec.targetRef,
      projectRefs,
    ),
    targetRef,
  };
}

export function buildBindingTargetRef(
  targetKind: BindingTargetKind,
  targetRef: string,
): AccessBindingTargetRef | undefined {
  const normalizedRef = targetRef.trim();
  if (!normalizedRef) {
    return undefined;
  }

  return {
    kind: targetKind === 'Component' ? 'Component' : 'Resource',
    ref: normalizedRef,
  };
}
